/**
 * Saved views over any kind — client↔server contract (Data Design PRD §6.1).
 *
 * The client sends INTENT (a kind + filter clauses + column selection), never
 * SQL. The route handler compiles it server-side through the single
 * saved-views.ts compiler (parameterized, field-allowlisted, workspace-scoped).
 * This mirrors why rows.* exists separately from sql.run — the compiler is the
 * security boundary, not the client.
 */

import { z } from "zod";
import type { Operation } from "../../types";

export const filterOpSchema = z.enum([
  "eq", "neq", "contains", "gt", "lt", "gte", "lte", "in", "is-null", "not-null",
]);

export const viewFilterSchema = z.object({
  field: z.string().min(1),
  op: filterOpSchema,
  value: z.unknown().optional(),
});
export type ViewFilterDto = z.infer<typeof viewFilterSchema>;

export const viewQuerySchema = z.object({
  match: z.enum(["all", "any"]).optional(),
  filters: z.array(viewFilterSchema),
});
export type ViewQueryDto = z.infer<typeof viewQuerySchema>;

export const columnTypeSchema = z.enum(["text", "number", "boolean", "date", "datetime", "money", "urn"]);

export const viewColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  type: columnTypeSchema,
});
export type ViewColumnDto = z.infer<typeof viewColumnSchema>;

export const viewSortSchema = z.object({ field: z.string(), dir: z.enum(["asc", "desc"]).optional() });

export const savedViewRowSchema = z.object({
  uid: z.string(),
  name: z.string(),
  entity_kind: z.string(),
  query_json: z.string(),
  columns_json: z.string().nullable(),
  sort_json: z.string().nullable(),
  visibility: z.enum(["private", "workspace"]),
  pinned_to: z.string().nullable(),
  origin: z.enum(["user", "agent", "system"]),
  created_by: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SavedViewRowDto = z.infer<typeof savedViewRowSchema>;

// ---------- list ----------

export const savedViewsListQuery = z.object({
  entity_kind: z.string().optional(),
});
export const savedViewsListResponse = z.object({ views: z.array(savedViewRowSchema) });
export const savedViewsList: Operation<
  void,
  z.infer<typeof savedViewsListQuery>,
  void,
  z.infer<typeof savedViewsListResponse>
> = {
  method: "GET",
  path: () => "/api/database/views",
  query: savedViewsListQuery,
  response: savedViewsListResponse,
};

// ---------- create ----------

export const savedViewCreateBody = z.object({
  name: z.string().min(1),
  entity_kind: z.string().min(1),
  query: viewQuerySchema,
  columns: z.array(viewColumnSchema).nullable().optional(),
  sort: z.array(viewSortSchema).nullable().optional(),
  visibility: z.enum(["private", "workspace"]).optional(),
});
export const savedViewCreateResponse = z.object({ view: savedViewRowSchema });
export const savedViewsCreate: Operation<
  void,
  void,
  z.infer<typeof savedViewCreateBody>,
  z.infer<typeof savedViewCreateResponse>
> = {
  method: "POST",
  path: () => "/api/database/views",
  body: savedViewCreateBody,
  response: savedViewCreateResponse,
  invalidates: [savedViewsList],
};

// ---------- run ----------

export const savedViewRunResponse = z.object({
  kind: z.string(),
  columns: z.array(viewColumnSchema),
  rows: z.array(z.record(z.unknown())),
  total: z.number(),
});
export type SavedViewRunResponse = z.infer<typeof savedViewRunResponse>;

/** Run one saved view by uid (query param), paginated. */
export const savedViewRunQuery = z.object({
  uid: z.string().min(1),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  offset: z.coerce.number().int().min(0).optional(),
});
export const savedViewsRun: Operation<
  void,
  z.infer<typeof savedViewRunQuery>,
  void,
  z.infer<typeof savedViewRunResponse>
> = {
  method: "GET",
  path: () => "/api/database/views/run",
  query: savedViewRunQuery,
  response: savedViewRunResponse,
};

export const savedViews = {
  list: savedViewsList,
  create: savedViewsCreate,
  run: savedViewsRun,
};
